import React, { useCallback, useEffect, useState } from "react";
import { API_URL } from "../../config";
import { LEGAL_FISCAL_MARKERS } from "../../constants/legalFiscalMarkers";
import { getMessage } from "../../data/locales";
import { getLegalTextsApiPath } from "../../utils/publicSiteConfigPaths";
import { LEGAL_TEXT_DEFAULTS } from "../../hooks/useLegalPageContent";

const FIELD_LABELS = {
  terminosHtml: "Contenido HTML de términos y condiciones",
  privacidadHtml: "Contenido HTML de política de privacidad",
  terminosUpdatedLabel: "Etiqueta de actualización (términos)",
  privacidadUpdatedLabel: "Etiqueta de actualización (privacidad)",
};

function TextosLegales() {
  const [form, setForm] = useState(LEGAL_TEXT_DEFAULTS);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadTexts = useCallback(async () => {
    try {
      const res = await fetch(getLegalTextsApiPath(), { credentials: "include" });
      if (!res.ok) {
        setError(`No se pudieron cargar los textos legales (${res.status}).`);
        return;
      }
      const data = await res.json();
      setForm({ ...LEGAL_TEXT_DEFAULTS, ...data });
    } catch {
      setError("No se pudieron cargar los textos legales.");
    }
  }, []);

  useEffect(() => {
    loadTexts();
  }, [loadTexts]);

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMensaje("");
    setError("");
    try {
      const res = await fetch(getLegalTextsApiPath(), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "No se pudieron guardar los textos legales.");
        return;
      }
      setForm({ ...LEGAL_TEXT_DEFAULTS, ...data.textosLegales });
      setMensaje("Textos legales guardados correctamente.");
    } catch {
      setError("Error de conexión al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="cfgx-section">
      <h2>Textos legales</h2>
      <p className="cfgx-help">
        Edita el contenido HTML de las páginas de términos y condiciones y política de privacidad.
        Las páginas públicas cargan estos textos desde {API_URL}/api/configuracion/textos-legales.
      </p>
      <div className="cfgx-legal-markers">
        <p id="legal-fiscal-markers-title" className="cfgx-legal-markers-title">
          {getMessage("legalTexts.markersTitle")}
        </p>
        <ul aria-labelledby="legal-fiscal-markers-title">
          {LEGAL_FISCAL_MARKERS.map((marker) => (
            <li key={marker.token}>
              <code>{marker.token}</code>
              <span>{getMessage(marker.labelKey)}</span>
            </li>
          ))}
        </ul>
        <p>{getMessage("legalTexts.markersSource")}</p>
      </div>
      {Object.keys(FIELD_LABELS).map((key) => (
        <div className="cfgx-field" key={key}>
          <label htmlFor={`legal-text-${key}`}>{FIELD_LABELS[key]}</label>
          {key.endsWith("Html") ? (
            <textarea
              id={`legal-text-${key}`}
              className="cfgx-textarea cfgx-textarea--tall"
              rows={12}
              value={form[key]}
              onChange={(e) => onChange(key, e.target.value)}
            />
          ) : (
            <input
              id={`legal-text-${key}`}
              type="text"
              className="cfgx-input"
              value={form[key]}
              onChange={(e) => onChange(key, e.target.value)}
            />
          )}
        </div>
      ))}
      {error ? <p className="cfgx-error">{error}</p> : null}
      {mensaje ? <p className="cfgx-msg">{mensaje}</p> : null}
      <button type="button" className="cfgx-btn-guardar" onClick={handleSave} disabled={saving}>
        {saving ? "Guardando…" : "Guardar textos legales"}
      </button>
    </section>
  );
}

export default TextosLegales;
